import { definePreset } from '@openng/optimus-ui-themes';
import Aura from '@openng/optimus-ui-themes/aura';

export const themePreset = definePreset(Aura, {
  primitive: {
    emerald: {
      50: '#f6f6f6',
      100: '#d1d1d1',
      200: '#adadad',
      300: '#898989',
      400: '#656565',
      500: '#414141',
      600: '#373737',
      700: '#2e2e2e',
      800: '#242424',
      900: '#1a1a1a',
      950: '#101010',
    },
    red: {
      50: '#fce8f0',
      100: '#f9c7e0',
      200: '#f5a8d8',
      300: '#ff6687',
      400: '#ff3360',
      500: '#e20039',
      600: '#d9003f',
      700: '#c60038',
      800: '#b30030',
      900: '#8e0025',
      950: '#65001a',
    },
  },
  semantic: {
    primary: {
      50: '{emerald.50}',
      100: '{emerald.100}',
      200: '{emerald.200}',
      300: '{emerald.300}',
      400: '{emerald.400}',
      500: '{emerald.500}',
      600: '{emerald.600}',
      700: '{emerald.700}',
      800: '{emerald.800}',
      900: '{emerald.900}',
      950: '{emerald.950}',
    },
    colorScheme: {
      dark: {
        surface: {
          50: '#f9f9f9',
          100: '#e0e0e0',
          200: '#c7c7c7',
          300: '#a6a6a6',
          400: '#7f7f7f',
          500: '#464646',
          600: '#3b3b3b',
          700: '#2f2f2f',
          800: '#232323',
          900: '#1a1a1a',
          950: '#0d0d0d',
        },
        primary: {
          color: '{primary.500}',
          contrastColor: '{surface.0}',
          hoverColor: '{primary.400}',
          activeColor: '{primary.300}',
        },
        highlight: {
          background: '{primary.500}',
          focusBackground: '{primary.400}',
          color: '{text.color}',
          focusColor: '{text.color}',
        },
        formField: {
          placeholderColor: '{surface.400}',
          invalidPlaceholderColor: '{surface.400}',
        },
        text: {
          color: '{surface.0}',
          hoverColor: '{surface.0}',
          mutedColor: '{surface.400}',
          hoverMutedColor: '{surface.300}',
        },
        content: {
          background: '{surface.800}',
          hoverBackground: '{surface.700}',
          borderColor: '{surface.500}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}',
        },
        list: {
          option: {
            focusBackground: '{surface.800}',
            selectedBackground: '{highlight.background}',
            selectedFocusBackground: '{highlight.focus.background}',
            color: '{text.color}',
            focusColor: '{text.color}',
            selectedColor: '{text.color}',
            selectedFocusColor: '{text.color}',
          },
        },
      },
    },
  },
  components: {
    fontFamily: {
      base: 'var(--font-heading)',
    },
    button: {
      root: {
        gap: '0.5rem',
        paddingX: '1rem',
        paddingY: '0.425rem',
        iconOnlyWidth: '3rem',
        label: {
          fontWeight: '500',
        },
      },
      colorScheme: {
        dark: {
          root: {
            primary: {
              background: '{red.500}',
              hoverBackground: '{red.400}',
              activeBackground: '{red.300}',
              borderColor: '{red.500}',
              hoverBorderColor: '{red.400}',
              activeBorderColor: '{red.300}',
              color: '{surface.0}',
              hoverColor: '{surface.0}',
              activeColor: '{surface.0}',
            },
            secondary: {
              background: '{primary.500}',
              hoverBackground: '{primary.400}',
              activeBackground: '{primary.300}',
              borderColor: '{primary.500}',
              hoverBorderColor: '{primary.400}',
              activeBorderColor: '{primary.300}',
              color: '{surface.0}',
              hoverColor: '{surface.0}',
              activeColor: '{surface.0}',
            },
          },
          text: {
            plain: {
              hoverBackground: '{surface.600}',
              activeBackground: '{surface.500}',
            },
          },
        },
      },
    },
    card: {
      root: {
        borderRadius: '0',
      },
      body: {
        padding: '0',
      },
    },
    menu: {
      item: {
        focusBackground: '{content.hover.background}',
        focusColor: '{content.hover.color}',
        icon: {
          focusColor: '{content.hover.color}',
        },
      },
    },
    datatable: {
      header: {
        background: '{surface.600}',
        borderColor: '{datatable.border.color}',
        color: '{surface.200}',
        borderWidth: '1px 0 1px 0',
        padding: '0.75rem 1rem',
      },
      headerCell: {
        background: '{surface.600}',
        hoverBackground: '{surface.600}',
        selectedBackground: '{surface.600}',
        borderColor: '{datatable.border.color}',
        color: '{surface.200}',
        hoverColor: '{content.hover.color}',
        selectedColor: '{surface.200}',
        gap: '0.5rem',
        padding: '0.75rem 1rem',
        focusRing: {
          width: '{focus.ring.width}',
          style: '{focus.ring.style}',
          color: '{focus.ring.color}',
          offset: '-1px',
          shadow: '{focus.ring.shadow}',
        },
      },
      columnTitle: {
        fontWeight: '700',
      },
      row: {
        background: '{content.background}',
        hoverBackground: '{content.hover.background}',
        selectedBackground: '{highlight.background}',
        color: '{content.color}',
        hoverColor: '{content.hover.color}',
        selectedColor: '{highlight.color}',
        focusRing: {
          width: '{focus.ring.width}',
          style: '{focus.ring.style}',
          color: '{focus.ring.color}',
          offset: '-1px',
          shadow: '{focus.ring.shadow}',
        },
      },
      bodyCell: {
        borderColor: '{datatable.border.color}',
        padding: '0.75rem 1rem',
      },
      columnResizer: {
        width: '0.5rem',
      },
      sortIcon: {
        color: '{text.muted.color}',
        hoverColor: '{text.hover.muted.color}',
        size: '0.875rem',
      },
      colorScheme: {
        dark: {
          root: {
            borderColor: '{surface.600}',
          },
          row: {
            stripedBackground: '{surface.950}',
          },
          bodyCell: {
            selectedBorderColor: '{primary.900}',
          },
        },
      },
    },
    paginator: {
      navButton: {
        hoverBackground: '{surface.700}',
        selectedBackground: '{surface.600}',
        color: '{text.color}',
        selectedColor: '{text.color}',
        hoverColor: '{text.hover.color}',
      },
    },
    progressbar: {
      value: {
        background: '{red.500}',
      },
    },
    progressspinner: {
      colorScheme: {
        dark: {
          root: {
            colorOne: '{surface.500}',
            colorTwo: '{surface.500}',
            colorThree: '{surface.500}',
            colorFour: '{surface.500}',
          },
        },
      },
    },
    tabs: {
      tablist: {
        background: 'transparent',
        borderWidth: '0',
      },
      tab: {
        background: 'transparent',
        borderWidth: '0',
        margin: '0',
        padding: '1.25rem 1rem',
        color: '{text.muted.color}',
        hoverColor: '{text.color}',
        activeColor: '{text.color}',
      },
      activeBar: {
        height: '2px',
        bottom: '0',
        background: '{red.500}',
      },
    },
    radiobutton: {
      icon: {
        checkedColor: '{red.500}',
        checkedHoverColor: '{red.400}',
      },
    },
    tooltip: {
      root: {
        maxWidth: '{unset}',
      },
    },
  },
});
